import React, { Fragment, useEffect, useState } from 'react'
import './style.sass'
import { useDispatch, useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import { useHistory, useParams } from 'react-router-dom'
import { SelectField } from '../../../common/FormFields'
import { getReleaseById } from '../../../../redux/actions/release'
import { getApplicationReleasePlatforms, publishToPlatforms } from '../../../../redux/actions/publish'
import SectionHeader from '../../../common/SectionHeader'
import LoadingBlock from '../../../common/LoadingBlock'
import LinkButton from '../../../common/LinkButton'
import SearchField from '../../../common/SearchField'
import Table from '../../../common/Table'
import IconTitle from '../../../common/IconTitle'
import FloatingBottomActionsBar from '../../../common/FloatingBottomActionsBar'
import VersionApprovedDate from './VersionApprovedDate'
import AppReleasePlatformStatus from '../../../../constants/AppReleasePlatformStatus'
import ReleaseStatus from '../../../../constants/ReleaseStatus'
import ConfirmationModal from '../../../common/ConfirmationModal'

const tableHeaders = [
  'Platform name',
  'Status',
  'Requested by developer',
  'Action'
]

const Publish = () => {
  // ===== Hooks =====
  const { releaseId } = useParams()
  const history = useHistory()

  // ===== Redux state =====
  const dispatch = useDispatch()
  const isPublishing = useSelector(({ publish }) => publish.isPublishing)
  const allPlatforms = useSelector(({ publish }) => publish.allPlatforms)
  const applicationReleasePlatforms = useSelector(({ publish }) => publish.applicationReleasePlatforms)
  const isFetchingAppReleasePlatforms = useSelector(({ publish }) => publish.isFetchingAppReleasePlatforms)
  const release = useSelector(({ release }) => release.release)
  const isFetchingCurrentRelease = useSelector(({ release }) => release.isFetchingCurrentRelease)
  const releasesForApp = useSelector(({ release }) => release.releasesForApp)
  const isFetchingReleasesForApp = useSelector(({ release }) => release.isFetchingReleasesForApp)

  // ==== Local state ====
  const [selectedPlatforms, setSelectedPlatforms] = useState([])
  const [filterText, setFilterText] = useState('')
  const [isConfirmPublishModalOpen, setIsConfirmPublishModalOpen] = useState(false)
  const [tableDataExistingPlatforms, setTableDataExistingPlatforms] = useState([])
  const [tableDataOtherPlatforms, setTableDataOtherPlatforms] = useState([])

  // ===== Side effects =====
  useEffect(() => {
    if (releaseId && release?.id?.toString() !== releaseId) {
      dispatch(getReleaseById(releaseId))
    }
  }, [releaseId])

  useEffect(() => {
    if (releaseId && !isPublishing) {
      dispatch(getApplicationReleasePlatforms(releaseId))
    }
  }, [releaseId, isPublishing])

  useEffect(() => {
    if (release && !isFetchingAppReleasePlatforms && allPlatforms && applicationReleasePlatforms) {
      computeTableData()
    }
  }, [release, allPlatforms])

  // ===== Methods =====
  const onVersionChange = (value) => {
    const path = history.location?.pathname?.replace(/[0-9]/g, '')
    history.push(`${path}${value}`)
  }

  const generateTableRows = (tableData) => {
    const filteredTableData = getFilteredTableData(tableData)
    return filteredTableData.map(data => ({
      id: data?.id,
      orignal: data,
      data: [
        data.name,
        getStatusComponent(data.status),
        getIsRequestedComponent(data.isRequestedByDeveloper),
        getIsActionComponent(data)
      ]
    }))
  }

  const getFilteredTableData = (tableData) => {
    if (!tableData || !Array.isArray(tableData)) return []

    return tableData.filter(x => {
      if (x?.name?.toLowerCase().includes(filterText?.toLowerCase())) {
        return true
      }
      return false
    })
  }

  const computeTableData = () => {
    const requestPlatformCodes = release?.requestPlatformCodes?.split(',')

    const data = allPlatforms?.map(platform => {
      // Finding if app already exist in platfrom
      const appExistInPlatfrom = !!applicationReleasePlatforms?.find(x => x.platformId === platform.id && x.releasePlatformStatus === AppReleasePlatformStatus.PUBLISHED)
      // Finding the app release platform record for the current selected release
      const appReleasePlatformForRelease = applicationReleasePlatforms?.find(x => x.platformId === platform.id && x.releaseId?.toString() === releaseId)
      // Finding if developer requested to publish to platform
      const isRequestedByDeveloper = !!requestPlatformCodes?.find(x => x === platform.code)

      return {
        id: platform.id,
        name: platform.name,
        status: appReleasePlatformForRelease?.releasePlatformStatus || AppReleasePlatformStatus.NOT_PUBLISHED,
        isRequestedByDeveloper,
        appExistInPlatfrom
      }
    })

    // Setting Table Data for Existing Platforms
    const dataExisting = data?.filter(x => x.appExistInPlatfrom)
    setTableDataExistingPlatforms(dataExisting)

    // Setting Table Data for Other Platforms
    const dataOther = data?.filter(x => !x.appExistInPlatfrom)
    setTableDataOtherPlatforms(dataOther)
  }

  const getStatusComponent = (status) => {
    if (!status) {
      return <IconTitle title="Not published" icon="clock-o" iconClass="icon-warning" />
    }
    switch (status) {
      case AppReleasePlatformStatus.NOT_PUBLISHED:
        return <IconTitle title="Not published" icon="clock-o" iconClass="icon-warning" />
      case AppReleasePlatformStatus.PUBLISHED:
        return <IconTitle title="Published" icon="check-circle-o" iconClass="icon-success" />
      case AppReleasePlatformStatus.EXPIRED:
        return <IconTitle title="Expired" icon="clock-o" iconClass="icon-warning" />
      case AppReleasePlatformStatus.ERROR:
        return <IconTitle title="Error" icon="times-circle-o" iconClass="icon-error" />
      default:
        return "-"
    }
  }

  const getIsRequestedComponent = (value) => {
    if (!value) {
      return <IconTitle title="No" icon="times-circle-o" iconClass="icon-error" />
    }
    return <IconTitle title="Yes" icon="check-circle-o" iconClass="icon-success" />
  }

  const getIsActionComponent = (platform) => {
    return (
      <LinkButton
        btnText="View Settings"
        onClick={
          () => history.push(`/admin/all-applications/publish/settings/${releaseId}/${platform.id}`)
        }
      />
    )
  }

  const handleSelectedRowsChange = (selectedRows) => {
    setSelectedPlatforms(selectedRows)
  }

  const getSelectedAndExistingPlatforms = () => {
    const existingPlatformsNotPublished = tableDataExistingPlatforms?.filter(x => x.status === AppReleasePlatformStatus.NOT_PUBLISHED)
    const selectedAndExistingPlatforms = [...selectedPlatforms, ...existingPlatformsNotPublished]
    return selectedAndExistingPlatforms
  }

  const onPublishClicked = () => {
    setIsConfirmPublishModalOpen(true)
  }

  const publish = async () => {
    const selectedAndExistingPlatforms = getSelectedAndExistingPlatforms()
    const platformIds = selectedAndExistingPlatforms?.map(x => x?.id)

    const res = await dispatch(publishToPlatforms(releaseId, platformIds))

    if (res) {
      toast.success('Successfully published!')
      setSelectedPlatforms([])
      return
    }
    toast.error('Something went wrong, Try again!')
  }

  const getApplication = () => {
    // Parsing release.data to get the application object
    let app = null
    try {
      app = release?.data ? JSON.parse(release?.data) : null
    } catch (e) {
      /* Fail Silently */
    }
    return app
  }

  const getStatusString = (status) => {
    switch (status) {
      case ReleaseStatus.APPROVED:
        return "Not Published"
      case ReleaseStatus.LIVE:
        return "Live"
      case ReleaseStatus.EXPIRED:
        return "Expired"
      default:
        return null
    }
  }

  const renderConfirmPublishModal = () => {
    return (
      <ConfirmationModal
        title="Publish version"
        description="This action will publish the application to the selected platforms, you do wish to proceed?"
        show={isConfirmPublishModalOpen}
        onConfirm={() => {
          setIsConfirmPublishModalOpen(false)
          publish()
        }}
        onCancel={() => setIsConfirmPublishModalOpen(false)}
      />
    )
  }

  const renderFloatingActionBar = () => {
    const selectedAndExistingPlatforms = getSelectedAndExistingPlatforms()

    if (selectedAndExistingPlatforms?.length > 0) {
      return (
        <FloatingBottomActionsBar
          mainText={`Please make sure you configured platform settings from above list before publishing.`}
          // Primary action
          primaryButtonText={`Publish to selected platforms (${selectedAndExistingPlatforms?.length})`}
          onPrimaryButtonPressed={onPublishClicked}
          isProccessingPrimaryAction={isPublishing}
        />
      )
    }
  }

  if (
    isFetchingAppReleasePlatforms ||
    isFetchingCurrentRelease ||
    !release ||
    release?.id?.toString() !== releaseId
  ) {
    return <LoadingBlock message="Loading..." />
  }
  return (
    <div className="publish-component-container">
      <SectionHeader
        title="Publish"
        subTitle={`v${release?.version?.toFixed(1)} - ${getApplication()?.name}`}
        backButtonOnClick={() => { history.push(`/admin/all-applications/${releaseId}`) }}
        status={getStatusString(release?.status)}
      />
      <SelectField
        displayName="Release version"
        placeholder="Select release version"
        onChange={(id) => onVersionChange(id)}
        options={Array.isArray(releasesForApp) ?
          releasesForApp?.map((release) => ({ value: release.id, displayName: `v${release.version?.toFixed(1)}` })) : []
        }
        value={parseInt(releaseId)}
        optionsLoading={isFetchingReleasesForApp}
      />
      <VersionApprovedDate
        release={release}
      />
      <SearchField
        placeholder="Search platforms"
        value={filterText}
        onChange={(value) => setFilterText(value)}
      />
      {!tableDataExistingPlatforms?.length || (
        <Table
          title="Application already exist in below platforms"
          headers={tableHeaders}
          rows={generateTableRows(tableDataExistingPlatforms)}
          disableHoverEffect={true}
          selectionVisible={true}
          selectionDisabled={true}
          defaultSelectedRows={tableDataExistingPlatforms}
        />
      )}
      {!(release.status === ReleaseStatus.APPROVED || release.status === ReleaseStatus.LIVE) || (
        <Fragment>
          <Table
            title={tableDataExistingPlatforms?.length ? "Other available platforms" : null}
            headers={tableHeaders}
            rows={generateTableRows(tableDataOtherPlatforms)}
            disableHoverEffect={true}
            selectionVisible={true}
            onSelectedRowsChange={selectedRows => handleSelectedRowsChange(selectedRows)}
          />
          {renderFloatingActionBar()}
          {renderConfirmPublishModal()}
        </Fragment>
      )}
      <div className='spacer' />
    </div>
  )
}

export default Publish
